<docs>
  # UiKit - Parallax
  > *generic way to create basic parallax effect
</docs>
  
<script lang="ts">
import { defineComponent, ref, toRefs, PropType } from 'vue';
import { templateRef, /* useIntersectionObserver, */ debouncedWatch } from '@vueuse/core';
import { tryOnUnmounted } from '@vueuse/shared';
import interfaceStore from 'store/interface';
import useFlow from 'utils/flow';
import { gsap, ScrollTrigger } from 'vendors/gsap';

const UiParallax = defineComponent({
  name: "UiParallax",
  props: {
    /**
      * Force a specific tag on the element. (semantic purpose)
    */
    tag: {
      type: String,
      default: 'div'
    },
    /**
      * Scrub speed, time to catch up scroll progress in seconds -> aka lag/lerp
      * 0 = same as scroll
      * 1 = 1 second to catch up scroll progress
    */
    scrub: {
      type: Number,
      default: 0.8,
    },
    /**
      * parallax speed, negative value = inverse progression
      * everything is based on total distance to go.
      * `1 = 25%` of viewport height
      * `2 = 50%` of viewport height
      * `0 = 0`
    */
    speed: {
      type: Number,
      default: 1,
    },
    /**
      * direction 
      * `1 = y`
      * `-1 = x`
    */
    direction: {
      type: Number as PropType<1 | -1>,
      default: 1,
      validator(value) {
        // 1 or -1
        return value === 1 || value === -1;
      }
    }
  },
  setup(props){
    const el = templateRef<HTMLElement>('el');
    const st = ref<ScrollTrigger>();
    const { viewport } = toRefs(interfaceStore.state);
    const { onInitUi } = useFlow();

    onInitUi( () => {
      if (el.value && !st.value) {
        // based on viewport height value, -> will normalise every parallax element globally. ratio * 25% viewport height
        const total = props.speed * (viewport.value.innerHeight * 0.25);
        // we can switch based on element height using yPercent/xPercent instead with this :
        // const total = props.speed * 100;
        if (props.direction === 1) {
          st.value = ScrollTrigger.create({
            trigger: el.value,
            scrub: props.scrub,
            animation: gsap.fromTo(el.value, {y: -(total/2)}, {y:(total/2), ease: 'none'})
            // animation: gsap.fromTo(el.value, {yPercent: -(total/2)}, {yPercent:(total/2), ease: 'none'})
          });
        } else {
          st.value = ScrollTrigger.create({
            trigger: el.value,
            scrub: props.scrub,
            animation: gsap.fromTo(el.value, {x: -(total/2)}, {x:(total/2), ease: 'none'})
            // animation: gsap.fromTo(el.value, {xPercent: -(total/2)}, {xPercent:(total/2), ease: 'none'})
          });
        }

        if (!interfaceStore.isLayoutDesktop) {
          st.value.disable();
          gsap.set(el.value, {clearProps: 'all'});
        }
      }
    })

    debouncedWatch(
      () => viewport.value.width,
      () => {
        if (st.value) {
          if (interfaceStore.isLayoutDesktop) {
            st.value.enable();
          } else {
            st.value.disable();
            gsap.set(el.value, {clearProps: 'all'});
          }
        }
      },
      { debounce: 500, deep: true }
    )

    tryOnUnmounted( () => {
      st.value?.kill(false);
    })
  }
});
export default UiParallax
</script>
  
<template>
  <component :is="tag"
             ref="el"
             class="parallax"
  >
    <slot />
  </component>
</template>
